import { RenderedGame } from '@mythos/game/Game';
import {
  applyGameUpdateToGameViewModel,
  constructGameViewModelFromRenderedGame,
  GameViewModel,
} from '@mythos/game/GameViewModel';
import { GameUpdateMessage } from '@mythos/game/Messages';
import { create } from 'zustand';

interface GameStore {
  gameByID: Record<string, GameViewModel>;
  gameErrorByID: Record<string, string>;

  storeGame: (game: GameViewModel) => void;
  fetchGame: (gameID: string, sequenceID?: number | null) => Promise<void>;
  applyGameUpdate: (update: GameUpdateMessage) => void;
}

export const useGameStore = create<GameStore>()((set, get) => ({
  gameByID: {},
  gameErrorByID: {},

  storeGame: (game) => {
    if (!game) return;
    const existing = get().gameByID[game.id];
    if (existing && existing.sequenceID >= game.sequenceID) return;
    set((state) => ({
      gameByID: { ...state.gameByID, [game.id]: game },
    }));
  },

  fetchGame: async (gameID, sequenceID) => {
    const existing = get().gameByID[gameID];
    if (existing && existing.sequenceID >= (sequenceID || 0)) return;
    if (get().gameErrorByID[gameID]) return;

    const response = await fetch(`/api/game/${gameID}/state`, {
      credentials: 'same-origin',
    });
    if (!response.ok) {
      set((state) => ({
        gameErrorByID: { ...state.gameErrorByID, [gameID]: 'Game not found' },
      }));
      return;
    }
    const game: RenderedGame = await response.json();
    const gameViewModel = constructGameViewModelFromRenderedGame(game);
    get().storeGame(gameViewModel);
  },

  applyGameUpdate: (update) => {
    const existing = get().gameByID[update.gameID];
    if (!existing) {
      console.warn(`Game update for unknown game ${update.gameID}`);
      return;
    }
    if (existing.sequenceID > update.sequenceID) {
      console.info(
        `Throwing away game update for game ${update.gameID} with sequenceID ${update.sequenceID} but expected ${existing.sequenceID}`,
      );
      return;
    }
    if (existing.sequenceID < update.targetSequenceID) {
      console.warn(
        `Game update for game ${update.gameID} with sequenceID ${update.sequenceID} but expected ${existing.sequenceID}`,
      );
      return;
    }
    const newGame = applyGameUpdateToGameViewModel(existing, update);
    get().storeGame(newGame);
  },
}));
